/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import ImageUpload from '@/components/ImageUpload';
import { usePlatform } from '@/hooks/usePlatform';
import { CHECKOUT_LANGUAGES, getT } from '@/lib/checkoutI18n';
import { getLumenT, LUMEN_ICONS } from '@/lib/lumenI18n';
import { toast } from 'sonner';
import { BrandAnalyzer } from '@/components/admin/BrandAnalyzer';
import { Loader2, Save, RefreshCw, Monitor, Smartphone, Palette, Sparkles, Plus, Trash2, Store as StoreIcon, DownloadCloud, ShoppingBag, Star, Wand2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_UI = {
  template: 'classic', favicon_url: '', help_text: '', guarantee_text: '', pay_button_text: '',
  promo_bar_items: [], show_promo_bar: true, show_steps: true, show_express: true,
  ab_test_enabled: false, ab_variant_a: 'classic', ab_variant_b: 'lumen',
  trust_items: [], footer_items: [], trustpilot_enabled: false,
  trustpilot_label: 'Excellent', trustpilot_rating: '4.8', trustpilot_reviews: '',
  i18n: {}, brand: {},
};

const AB_TEMPLATES = [{ k: 'classic', n: 'Classique' }, { k: 'lumen', n: 'Lumen' }, { k: 'shopify', n: 'Shopify' }, { k: 'brand', n: 'Sur mesure' }];
const tplName = (k) => (AB_TEMPLATES.find((x) => x.k === k) || {}).n || k;

// Built-in translated defaults for a given language (used as the editing baseline
// and as the checkout fallback when a language has no override).
const localizedDefaults = (lang) => {
  const lt = getLumenT(lang || 'fr');
  const t = getT(lang || 'fr');
  return {
    trust_items: lt.trust.map((x) => ({ ...x })),
    footer_items: lt.footer.map((x) => ({ ...x })),
    promo_bar_items: [t.trust_secure, t.trust_fast_ship, t.trust_money_back],
    help_text: lt.help,
    guarantee_text: lt.guarantee,
    pay_button_text: lt.complete_order,
  };
};

const normalizeUI = (ui, lang) => {
  const sl = lang || 'fr';
  const base = { ...DEFAULT_UI, ...(ui || {}) };
  base.i18n = { ...(ui?.i18n || {}) };
  // One-time migration: move legacy flat overrides (single language) into the
  // store's language bucket so existing customisation isn't lost.
  if (!base.i18n[sl]) {
    const raw = ui || {};
    const seed = {};
    ['trust_items', 'footer_items', 'promo_bar_items'].forEach((k) => { if (raw[k] && raw[k].length) seed[k] = raw[k]; });
    ['help_text', 'guarantee_text', 'pay_button_text'].forEach((k) => { if (raw[k]) seed[k] = raw[k]; });
    if (Object.keys(seed).length) base.i18n[sl] = seed;
  }
  return base;
};

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-xs font-semibold text-zinc-700">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-zinc-400 mt-1">{hint}</p>}
  </div>
);

const ItemEditor = ({ title, items, onChange, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {items.map((it, i) => (
      <div key={i} className="border border-zinc-200 rounded-lg p-2.5 space-y-2 bg-zinc-50/60">
        <div className="flex gap-2">
          <select
            value={it.icon}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, icon: e.target.value } : x)))}
            className="h-8 rounded-md border border-zinc-200 px-2 text-xs bg-white w-24 shrink-0"
            data-testid={`${testid}-icon-${i}`}
          >
            {LUMEN_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
          </select>
          <Input
            value={it.title}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
            placeholder="Titre"
            className="h-8 text-xs flex-1"
            data-testid={`${testid}-title-${i}`}
          />
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-600 shrink-0 px-1">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <Input
          value={it.subtitle}
          onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, subtitle: e.target.value } : x)))}
          placeholder="Description"
          className="h-8 text-xs"
          data-testid={`${testid}-subtitle-${i}`}
        />
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" className="w-full h-8 text-xs" onClick={() => onChange([...items, { icon: 'shield', title: '', subtitle: '' }])} data-testid={`${testid}-add`}>
      <Plus className="h-3.5 w-3.5 mr-1" /> {title}
    </Button>
  </div>
);

export default function CheckoutDesign() {
  const { matchStore, label } = usePlatform();
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [previewKey, setPreviewKey] = useState(0);
  const [abResults, setAbResults] = useState(null);
  const [loadingAb, setLoadingAb] = useState(false);
  const [importing, setImporting] = useState(false);
  const [designLang, setDesignLang] = useState('fr');

  const platformStores = useMemo(() => stores.filter(matchStore), [stores, matchStore]);

  useEffect(() => {
    (async () => {
      try {
        const [st, pr] = await Promise.all([api.get('/stores'), api.get('/products')]);
        setStores(st.data || []);
        setProducts(pr.data || []);
      } catch {
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Auto-select the first store of the active platform.
  useEffect(() => {
    if (!platformStores.length) { setSelectedId(''); setForm(null); return; }
    if (!platformStores.find((s) => s.id === selectedId)) {
      setSelectedId(platformStores[0].id);
    }
  }, [platformStores]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the selected store into the editable form.
  useEffect(() => {
    const s = stores.find((x) => x.id === selectedId);
    if (!s) return;
    setForm({ ...s, checkout_ui: normalizeUI(s.checkout_ui, s.language) });
    setDesignLang(s.language || 'fr');
  }, [selectedId, stores]);

  const setUI = (patch) => setForm((f) => ({ ...f, checkout_ui: { ...f.checkout_ui, ...patch } }));

  const fetchAb = async (id) => {
    if (!id) return;
    setLoadingAb(true);
    try {
      const { data } = await api.get(`/stores/${id}/ab-test`);
      setAbResults(data);
    } catch {
      setAbResults(null);
    } finally {
      setLoadingAb(false);
    }
  };

  useEffect(() => { if (selectedId) fetchAb(selectedId); }, [selectedId]);

  const importBranding = async () => {
    if (!form) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/stores/${form.id}/import-branding`);
      setForm((f) => ({
        ...f,
        logo_url: data.logo_url || f.logo_url,
        checkout_ui: { ...f.checkout_ui, favicon_url: data.favicon_url || f.checkout_ui.favicon_url },
      }));
      setStores((prev) => prev.map((s) => (s.id === form.id
        ? { ...s, logo_url: data.logo_url || s.logo_url, checkout_ui: { ...(s.checkout_ui || {}), favicon_url: data.favicon_url || (s.checkout_ui || {}).favicon_url } }
        : s)));
      toast.success('Logo et favicon importés depuis la boutique');
      setPreviewKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'importer depuis la boutique");
    } finally {
      setImporting(false);
    }
  };

  const previewProductId = useMemo(() => {
    const p = products.find((x) => x.store_id === selectedId && x.active !== false);
    return p?.id || '';
  }, [products, selectedId]);

  const previewUrl = previewProductId
    ? `${BACKEND}/checkout/${previewProductId}?pv=1&pv_tpl=${form?.checkout_ui?.template || 'classic'}&_p=${previewKey}`
    : '';

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const { _id, created_at, ...payload } = form; // eslint-disable-line no-unused-vars
      const { data } = await api.put(`/stores/${form.id}`, payload);
      setStores((prev) => prev.map((s) => (s.id === form.id ? { ...s, ...data } : s)));
      toast.success('Personnalisation enregistrée');
      setPreviewKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-screen flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-zinc-400" /></div>;
  }

  const ui = form?.checkout_ui || DEFAULT_UI;
  // Per-language customisation bucket for the language currently being edited.
  const loc = (ui.i18n && ui.i18n[designLang]) || {};
  const dft = localizedDefaults(designLang);
  const setLoc = (patch) => setUI({ i18n: { ...(ui.i18n || {}), [designLang]: { ...loc, ...patch } } });
  // A/B duel: two freely-chosen variants (defaults classic vs current template).
  const abA = ui.ab_variant_a || 'classic';
  const abB = ui.ab_variant_b || (ui.template && ui.template !== 'classic' ? ui.template : 'lumen');
  const abVariants = [abA, ...(abB !== abA ? [abB] : [])];

  return (
    <div className="flex flex-col lg:flex-row lg:h-screen lg:overflow-hidden bg-zinc-100">
      {/* Settings panel */}
      <div className="w-full lg:w-[420px] bg-white lg:border-r border-zinc-200 lg:overflow-y-auto shrink-0 flex flex-col" data-testid="checkout-design-panel">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-5 py-4 z-10">
          <div className="flex items-center gap-2 mb-1">
            <Palette className="h-5 w-5" />
            <h1 className="font-display text-lg font-bold">Personnalisation Checkout</h1>
          </div>
          <p className="text-xs text-zinc-500">{label || 'Boutiques'} · branding, modèle et contenus</p>
        </div>

        {!platformStores.length ? (
          <div className="p-6 text-sm text-zinc-500">Aucune boutique pour cette plateforme. Créez d'abord une boutique.</div>
        ) : !form ? null : (
          <div className="p-5 space-y-6">
            {/* Store selector */}
            <Field label="BOUTIQUE">
              <div className="relative mt-1.5">
                <StoreIcon className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="h-10 w-full rounded-md border border-zinc-200 pl-9 pr-3 text-sm bg-white"
                  data-testid="checkout-design-store-select"
                >
                  {platformStores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </Field>

            {/* Template switcher */}
            <div>
              <Label className="text-xs font-semibold text-zinc-700">MODÈLE DE CHECKOUT</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="admin-template-switcher">
                {[{ k: 'classic', n: 'Classique', d: 'Une colonne, sobre' }, { k: 'lumen', n: 'Lumen', d: 'Conversion, confiance' }, { k: 'shopify', n: 'Shopify', d: 'Style natif, rassurant' }, { k: 'brand', n: 'Sur mesure', d: 'Couleurs & logo de ta boutique' }].map((tpl) => (
                  <button
                    key={tpl.k}
                    type="button"
                    onClick={() => setUI({ template: tpl.k })}
                    data-testid={`template-option-${tpl.k}`}
                    className={`text-left p-3 rounded-lg border-2 transition ${ui.template === tpl.k ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                  >
                    <div className="flex items-center gap-1.5 font-semibold text-sm">{tpl.k === 'lumen' && <Sparkles className="h-3.5 w-3.5" />}{tpl.k === 'shopify' && <ShoppingBag className="h-3.5 w-3.5" />}{tpl.k === 'brand' && <Wand2 className="h-3.5 w-3.5" />}{tpl.n}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{tpl.d}</div>
                  </button>
                ))}
              </div>
            </div>

            <BrandAnalyzer
              form={form}
              onApply={(patch) => setForm((f) => ({ ...f, ...patch }))}
              onBrandChange={(b) => setUI({ brand: b })}
            />

            {/* A/B test — free duel between two templates */}
            <div className="rounded-lg border-2 border-dashed border-zinc-200 p-3 space-y-3" data-testid="ab-test-block">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-bold text-zinc-800 flex items-center gap-1.5">🧪 A/B TEST AUTOMATIQUE</Label>
                  <p className="text-[11px] text-zinc-400 mt-0.5">50/50 entre deux modèles au choix. Voyez lequel convertit le mieux.</p>
                </div>
                <Switch checked={ui.ab_test_enabled} onCheckedChange={(v) => setUI({ ab_test_enabled: v })} data-testid="design-toggle-ab-test" />
              </div>
              {ui.ab_test_enabled && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Variante A</Label>
                      <select
                        value={abA}
                        onChange={(e) => setUI({ ab_variant_a: e.target.value })}
                        className="mt-1 h-9 w-full rounded-md border border-zinc-200 px-2 text-sm bg-white"
                        data-testid="ab-variant-a"
                      >
                        {AB_TEMPLATES.map((tp) => <option key={tp.k} value={tp.k}>{tp.n}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Variante B</Label>
                      <select
                        value={abB}
                        onChange={(e) => setUI({ ab_variant_b: e.target.value })}
                        className="mt-1 h-9 w-full rounded-md border border-zinc-200 px-2 text-sm bg-white"
                        data-testid="ab-variant-b"
                      >
                        {AB_TEMPLATES.map((tp) => <option key={tp.k} value={tp.k}>{tp.n}</option>)}
                      </select>
                    </div>
                  </div>
                  {abA === abB ? (
                    <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="ab-same-warning">
                      Choisis deux modèles <strong>différents</strong> pour lancer un test valide (ex. Lumen vs Shopify).
                    </p>
                  ) : (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      Chaque visiteur voit au hasard « {tplName(abA)} » ou « {tplName(abB)} ». Changer les variantes réinitialise l'attribution des visiteurs.
                    </p>
                  )}
                </>
              )}
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Résultats</span>
                <button type="button" onClick={() => fetchAb(selectedId)} className="text-[11px] text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="ab-refresh">
                  <RefreshCw className={`h-3 w-3 ${loadingAb ? 'animate-spin' : ''}`} /> Actualiser
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="ab-results">
                {abVariants.map((v) => {
                  const r = abResults?.variants?.[v] || { views: 0, purchases: 0, conversion: 0, revenue: 0 };
                  const otherKey = v === abA ? abB : abA;
                  const other = abResults?.variants?.[otherKey] || { conversion: 0 };
                  const winning = r.views > 0 && r.conversion >= other.conversion && (r.conversion > 0 || other.conversion === 0);
                  return (
                    <div key={v} className={`rounded-lg border p-2.5 ${winning ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200 bg-white'}`} data-testid={`ab-card-${v}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold">{tplName(v)}</span>
                        {winning && r.views > 0 && <span className="text-[9px] font-bold text-emerald-700 uppercase">★ meilleur</span>}
                      </div>
                      <div className="text-2xl font-extrabold font-mono mt-1" data-testid={`ab-conv-${v}`}>{r.conversion}%</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{r.views} vues · {r.purchases} commandes</div>
                      <div className="text-[10px] text-zinc-500">{(r.revenue || 0).toFixed(2)} € encaissé</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t border-dashed border-zinc-200">
              <div className="flex items-center justify-between pt-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Branding</div>
                <Button type="button" variant="outline" size="sm" onClick={importBranding} disabled={importing} className="h-7 text-[11px]" data-testid="import-branding-btn">
                  {importing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <DownloadCloud className="h-3.5 w-3.5 mr-1" />}
                  Importer depuis la boutique
                </Button>
              </div>
              <Field label="NOM AFFICHÉ">
                <Input value={form.display_name || ''} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder={form.name} className="mt-1.5 h-10" data-testid="design-display-name" />
              </Field>
              <Field label="LANGUE DU CHECKOUT">
                <select value={form.language || 'fr'} onChange={(e) => setForm({ ...form, language: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white" data-testid="design-language">
                  {CHECKOUT_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </Field>
              <ImageUpload value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} label="LOGO" aspectClass="h-24" testId="admin-logo-upload" />
              <ImageUpload value={ui.favicon_url} onChange={(url) => setUI({ favicon_url: url })} label="FAVICON (icône onglet navigateur)" aspectClass="h-16" testId="admin-favicon-upload" />
              <Field label="COULEUR D'ACCENT" hint="Bouton Payer, badges, détails.">
                <div className="flex gap-2 mt-1.5">
                  <Input type="color" value={form.accent_color || '#0A0A0A'} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-10 w-14 rounded-md p-1 cursor-pointer" data-testid="admin-accent-color-picker" />
                  <Input value={form.accent_color || ''} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} placeholder="#0A0A0A" className="h-10 font-mono text-xs" />
                </div>
              </Field>
              <Field label="EMAIL DE SUPPORT">
                <Input type="email" value={form.support_email || ''} onChange={(e) => setForm({ ...form, support_email: e.target.value })} placeholder="contact@boutique.com" className="mt-1.5 h-10 text-sm" data-testid="design-support-email" />
              </Field>
            </div>

            {/* Texts */}
            <div className="space-y-3 pt-2 border-t border-dashed border-zinc-200">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 pt-2">Textes & barres</div>
              <div className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2.5 leading-relaxed" data-testid="design-lumen-i18n-note">
                ℹ️ Ces textes (barre promo, garantie, bouton payer, aide, liste confiance, footer) sont <strong>personnalisables par langue</strong>.
                Choisis une langue ci-dessous et édite-la : au checkout, le client verra ta version dans sa langue (sinon la traduction intégrée).
              </div>
              <Field label="LANGUE À PERSONNALISER">
                <select
                  value={designLang}
                  onChange={(e) => setDesignLang(e.target.value)}
                  className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 px-3 text-sm bg-white"
                  data-testid="design-customize-language"
                >
                  {CHECKOUT_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}{l.code === (form?.language || 'fr') ? ' · langue par défaut' : ''}</option>)}
                </select>
              </Field>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-zinc-700">Barre promotionnelle en haut</Label>
                <Switch checked={ui.show_promo_bar} onCheckedChange={(v) => setUI({ show_promo_bar: v })} data-testid="design-toggle-promo" />
              </div>
              <Field label="ÉLÉMENTS DE LA BARRE (un par ligne)">
                <textarea
                  value={((loc.promo_bar_items && loc.promo_bar_items.length) ? loc.promo_bar_items : dft.promo_bar_items).join('\n')}
                  onChange={(e) => setLoc({ promo_bar_items: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })}
                  placeholder="Paiement sécurisé&#10;Livraison rapide&#10;Satisfait ou remboursé"
                  rows={3}
                  className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm"
                  data-testid="admin-trust-text-input"
                />
              </Field>
              <Field label="TEXTE GARANTIE" hint="Affiché au-dessus du bouton de paiement.">
                <Input value={loc.guarantee_text || ''} onChange={(e) => setLoc({ guarantee_text: e.target.value })} placeholder={dft.guarantee_text} className="mt-1.5 h-9 text-sm" data-testid="design-guarantee" />
              </Field>
              <Field label="TEXTE DU BOUTON PAIEMENT" hint="Ex. Finaliser la commande. Le montant est ajouté automatiquement.">
                <Input value={loc.pay_button_text || ''} onChange={(e) => setLoc({ pay_button_text: e.target.value })} placeholder={dft.pay_button_text} className="mt-1.5 h-9 text-sm" data-testid="design-pay-button" />
              </Field>
              <Field label="TEXTE DU LIEN AIDE (header)">
                <Input value={loc.help_text || ''} onChange={(e) => setLoc({ help_text: e.target.value })} placeholder={dft.help_text} className="mt-1.5 h-9 text-sm" data-testid="design-help-text" />
              </Field>
            </div>

            {/* Lumen-only blocks */}
            <div className="space-y-3 pt-2 border-t border-dashed border-zinc-200">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 pt-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Modèle Lumen <span className="text-zinc-300 normal-case font-normal">· édition : {(CHECKOUT_LANGUAGES.find((l) => l.code === designLang) || {}).label}</span></div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-zinc-700">Indicateur d'étapes (1·2·3)</Label>
                <Switch checked={ui.show_steps} onCheckedChange={(v) => setUI({ show_steps: v })} data-testid="design-toggle-steps" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-zinc-700">Paiement express (wallet)</Label>
                <Switch checked={ui.show_express} onCheckedChange={(v) => setUI({ show_express: v })} data-testid="design-toggle-express" />
              </div>
              <Field label="LISTE CONFIANCE (colonne de droite)">
                <ItemEditor title="Ajouter un élément" items={(loc.trust_items && loc.trust_items.length) ? loc.trust_items : dft.trust_items} onChange={(v) => setLoc({ trust_items: v })} testid="design-trust-items" />
              </Field>
              <Field label="FOOTER (4 marqueurs)">
                <ItemEditor title="Ajouter un marqueur" items={(loc.footer_items && loc.footer_items.length) ? loc.footer_items : dft.footer_items} onChange={(v) => setLoc({ footer_items: v })} testid="design-footer-items" />
              </Field>
            </div>

            {/* Trustpilot — shared block available on all templates */}
            <div className="space-y-3 pt-2 border-t border-dashed border-zinc-200">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 pt-2 flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5" /> Bloc Trustpilot <span className="text-zinc-300 normal-case font-normal">· tous les thèmes</span>
              </div>
              <div className="border border-zinc-200 rounded-lg p-3 space-y-2.5 bg-zinc-50/60">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-zinc-700">Afficher le bloc Trustpilot</Label>
                  <Switch checked={ui.trustpilot_enabled} onCheckedChange={(v) => setUI({ trustpilot_enabled: v })} data-testid="design-toggle-trustpilot" />
                </div>
                {ui.trustpilot_enabled && (
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={ui.trustpilot_label || ''} onChange={(e) => setUI({ trustpilot_label: e.target.value })} placeholder="Excellent" className="h-8 text-xs" data-testid="design-tp-label" />
                    <Input value={ui.trustpilot_rating || ''} onChange={(e) => setUI({ trustpilot_rating: e.target.value })} placeholder="4.8" className="h-8 text-xs" data-testid="design-tp-rating" />
                    <Input value={ui.trustpilot_reviews || ''} onChange={(e) => setUI({ trustpilot_reviews: e.target.value })} placeholder="10 000 avis" className="h-8 text-xs" data-testid="design-tp-reviews" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {form && (
          <div className="sticky bottom-0 bg-white border-t border-zinc-200 p-4 space-y-2">
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="lg:hidden flex items-center justify-center gap-2 w-full h-11 rounded-md border border-zinc-300 text-zinc-700 text-sm font-semibold hover:bg-zinc-50 transition"
                data-testid="mobile-open-preview"
              >
                <Sparkles className="h-4 w-4" /> Ouvrir l'aperçu checkout
              </a>
            )}
            <Button onClick={save} disabled={saving} className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="admin-save-settings-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer la personnalisation
            </Button>
          </div>
        )}
      </div>

      {/* Live preview — desktop only (the full checkout iframe is too heavy for
          mobile Safari and triggers iOS tab reloads; mobile uses the button). */}
      <div className="hidden lg:flex flex-1 bg-zinc-100 p-4 lg:p-8 overflow-y-auto flex-col items-center min-h-0">
        <div className="w-full max-w-5xl flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Aperçu live ({ui.template})</span>
          <div className="flex items-center gap-2">
            <div className="flex bg-white rounded-md border border-zinc-200 p-0.5">
              <button onClick={() => setPreviewMode('desktop')} className={`p-1.5 rounded ${previewMode === 'desktop' ? 'bg-zinc-900 text-white' : 'text-zinc-500'}`} data-testid="preview-desktop"><Monitor className="h-4 w-4" /></button>
              <button onClick={() => setPreviewMode('mobile')} className={`p-1.5 rounded ${previewMode === 'mobile' ? 'bg-zinc-900 text-white' : 'text-zinc-500'}`} data-testid="preview-mobile"><Smartphone className="h-4 w-4" /></button>
            </div>
            <Button variant="outline" size="sm" className="h-8" onClick={() => setPreviewKey((k) => k + 1)} data-testid="preview-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualiser</Button>
          </div>
        </div>
        <p className="text-[11px] text-zinc-400 mb-3 text-center max-w-md">Le modèle (Classique/Lumen/Shopify/Sur mesure) se met à jour aussitôt ci-dessous. Pour les autres contenus (textes, couleurs, logo, confiance) cliquez sur <b>Enregistrer</b> pour les voir dans l'aperçu.</p>
        {previewUrl ? (
          <div className={`bg-white rounded-2xl shadow-2xl overflow-hidden border border-zinc-200 transition-all ${previewMode === 'mobile' ? 'w-[390px]' : 'w-full max-w-5xl'}`} style={{ height: '78vh' }}>
            <iframe key={previewKey} src={previewUrl} title="Aperçu checkout" className="w-full h-full" data-testid="admin-live-preview-frame" />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 text-center px-6">
            Aucun produit actif pour cette boutique.<br />Importez/créez un produit pour voir l'aperçu.
          </div>
        )}
      </div>
    </div>
  );
}
